import { LOGO_ALLOWED_MIME_TYPES, LOGO_MAX_FILE_BYTES, LOGO_MAX_FILE_LABEL } from '@tn/shared'

/**
 * Derive dark and light brand color variants from a primary hex color.
 * Mirrors the logic in src/main/ai/template.ts.
 */
export function deriveBrandingVariants(hex: string): {
  primaryColorDark: string
  primaryColorLight: string
} {
  function adjustColor(h: string, factor: number): string {
    const r = parseInt(h.slice(1, 3), 16)
    const g = parseInt(h.slice(3, 5), 16)
    const b = parseInt(h.slice(5, 7), 16)
    const adj = (c: number) =>
      factor > 1
        ? Math.min(255, Math.round(c + (255 - c) * (factor - 1)))
        : Math.round(c * factor)
    return `#${adj(r).toString(16).padStart(2, '0')}${adj(g).toString(16).padStart(2, '0')}${adj(b).toString(16).padStart(2, '0')}`
  }

  return {
    primaryColorDark: adjustColor(hex, 0.6),
    primaryColorLight: adjustColor(hex, 1.5),
  }
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read the logo file.'))
    reader.readAsDataURL(file)
  })
}

// file.type comes from the extension, so a mislabelled or corrupt file can pass the type
// check; decoding it is the only way to know it will render rather than show a broken icon.
function assertImageDecodes(dataUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => reject(new Error('That file could not be read as an image. Please choose a valid PNG, JPEG, or SVG.'))
    img.src = dataUrl
  })
}

/**
 * Validate and convert a client logo file to a base64 data URL for storage in ClientBranding.
 * Rejects with a user-facing message on unsupported type, oversized file, or undecodable image.
 */
export async function readLogoFile(file: File): Promise<string> {
  if (!(LOGO_ALLOWED_MIME_TYPES as readonly string[]).includes(file.type)) {
    throw new Error('Logo must be a PNG, JPEG, or SVG image.')
  }
  if (file.size > LOGO_MAX_FILE_BYTES) {
    throw new Error(`Logo file is too large — please use an image under ${LOGO_MAX_FILE_LABEL}.`)
  }
  const dataUrl = await readAsDataUrl(file)
  await assertImageDecodes(dataUrl)
  return dataUrl
}

function rgbToSaturation(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b) / 255
  const min = Math.min(r, g, b) / 255
  const l = (max + min) / 2
  if (max === min) return 0
  const d = max - min
  return l > 0.5 ? d / (2 - max - min) : d / (max + min)
}

function toHex(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
}

// Minimum HSL saturation for a pixel bucket to count as a brand-color candidate at all —
// filters out achromatic/no-signal logos (e.g. a plain black or silver wordmark) instead of
// reporting a muted neutral as the "logo color".
const SATURATION_FLOOR = 0.15

/**
 * Extracts the single most dominant, non-background color from a logo image — a candidate
 * for the "Use logo color" option, offered alongside whatever brand color is already set
 * (never applied automatically). Entirely client-side (canvas + data URL, no network call).
 * Scores by frequency × saturation (not frequency alone), so a large near-white or
 * near-black background doesn't win just because it covers the most pixels. Returns null
 * when nothing clears the saturation floor — the caller simply doesn't offer the toggle.
 */
export function extractLogoPalette(dataUrl: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      try {
        const size = 48
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        if (!ctx) { resolve(null); return }
        ctx.drawImage(img, 0, 0, size, size)
        const { data } = ctx.getImageData(0, 0, size, size)

        // Quantized-RGB → { r, g, b, count } buckets, skipping transparent/near-white/near-black pixels.
        const buckets = new Map<string, { r: number; g: number; b: number; count: number }>()
        for (let i = 0; i < data.length; i += 4) {
          const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]]
          if (a < 128) continue
          const max = Math.max(r, g, b), min = Math.min(r, g, b)
          if (max > 245 && min > 230) continue
          if (max < 20) continue
          const step = 16
          const qr = Math.round(r / step) * step
          const qg = Math.round(g / step) * step
          const qb = Math.round(b / step) * step
          const key = `${qr},${qg},${qb}`
          const existing = buckets.get(key)
          if (existing) existing.count++
          else buckets.set(key, { r: qr, g: qg, b: qb, count: 1 })
        }

        let best: { hex: string; score: number } | null = null
        for (const { r, g, b, count } of buckets.values()) {
          const saturation = rgbToSaturation(r, g, b)
          if (saturation < SATURATION_FLOOR) continue
          const score = count * saturation
          if (!best || score > best.score) {
            best = { hex: `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase(), score }
          }
        }
        resolve(best?.hex ?? null)
      } catch {
        resolve(null)
      }
    }
    img.onerror = () => resolve(null)
    img.src = dataUrl
  })
}
